<template>
  <div>
    <div class="h-50 flex items-center">
      <mux-player
        v-if="value && status.status !== 'done'"
        :key="value"
        class="h-full"
        stream-type="on-demand"
        :playback-id="value"
      ></mux-player>
      <div
        v-else
        class="p-5 h-full w-1/2 flex justify-center items-center text-center bg-gray-400 text-white"
      >
        {{
          status.status === 'done'
            ? 'Caricamento anteprima in corso...'
            : "Carica un video per visualizzare l'anteprima"
        }}
      </div>
    </div>

    <div class="mt-4 flex items-center gap-x-10">
      <button
        type="button"
        class="btn-default"
        @click="$refs.fileInput.click()"
        :disabled="status.status !== 'ready'"
      >
        {{ value ? 'Sostituisci il video' : 'Scegli un video' }}
      </button>
      <p v-if="status.status === 'loading'">
        Progresso: {{ parseInt(status.progress) }}%
      </p>
      <p v-else-if="status.status === 'processing'">
        Il video è stato caricato, preparazione in corso...
      </p>
      <p v-else-if="status.status === 'done'">
        Il video è stato caricato con successo!
      </p>
      <p v-else-if="status.status === 'ready' && clearable && value">
        <button class="underline" @click="$emit('input', null)">Rimuovi</button>
      </p>
      <p v-else-if="status.status === 'error'" class="text-error">
        Si è verificato un errore, riprova
      </p>
    </div>

    <input
      :id="inputId"
      type="file"
      class="hidden"
      ref="fileInput"
      accept="video/*"
      @change="onFileChange"
    />
  </div>
</template>

<script>
/** Packages */
import * as UpChunk from '@mux/upchunk'
import axios from 'axios'
import '@mux/mux-player'

export default {
  props: {
    value: {
      type: String,
      default: null,
    },
    inputId: {
      type: String,
      default: null,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      file: null,
      status: { status: 'ready' },
      interval: null,
    }
  },
  watch: {
    file(newFile) {
      if (newFile) {
        this.upload(newFile)
      }
    },
    status(newStatus) {
      switch (newStatus.status) {
        case 'error':
          setTimeout(() => {
            this.status = { status: 'ready' }
          }, 3000)
          break
        case 'done':
          this.$emit('input', newStatus.playbackId)
          setTimeout(() => {
            this.status = { status: 'ready' }
          }, 3000)
      }
    },
  },
  beforeDestroy() {
    this.clearInterval()
  },
  methods: {
    onFileChange(event) {
      this.file = event?.target?.files?.[0]
    },
    async upload() {
      if (this.status.status !== 'ready') return

      try {
        const { id: uploadId, url } = await this.createUploadUrl()
        const upload = UpChunk.createUpload({
          endpoint: url,
          file: this.file,
          chunkSize: 5120,
        })

        upload.on('error', err => {
          this.status = { status: 'error', error: err }
        })

        upload.on('progress', progress => {
          this.status = { status: 'loading', progress: progress.detail }
        })

        upload.on('success', async () => {
          const { playbackId, assetId } = await this.getPlaybackAndAssetsIds(
            uploadId,
          )
          this.process({ playbackId, assetId })
        })
      } catch (err) {
        this.status = { status: 'error', error: err }
      }
    },
    async process({ playbackId, assetId }) {
      try {
        this.status = { status: 'processing', playbackId, assetId }
        await this.waitUntilAssetIsReady(assetId)
        this.status = { status: 'done', playbackId }
      } catch (err) {
        this.status = { status: 'error', error: err }
      }
    },
    async createUploadUrl() {
      const {
        data: { id, url },
      } = await axios.get('/.netlify/functions/mux-upload-url')
      return { id, url }
    },
    async getPlaybackAndAssetsIds(uploadId) {
      const {
        data: { playbackId, assetId },
      } = await axios.get(
        `/.netlify/functions/mux-playback-id?uploadId=${uploadId}`,
      )
      return { playbackId, assetId }
    },
    async getAssetStatus(assetId) {
      const {
        data: { status },
      } = await axios.get(
        `/.netlify/functions/mux-asset-status?assetId=${assetId}`,
      )
      return status
    },
    waitUntilAssetIsReady(assetId) {
      return new Promise((resolve, reject) => {
        this.interval = setInterval(async () => {
          try {
            if (!this.interval) return
            const status = await this.getAssetStatus(assetId)
            if (!this.interval) return

            switch (status) {
              case 'preparing':
                return
              case 'ready':
                this.clearInterval()
                resolve()
                return
              case 'errored': {
                this.clearInterval()
                reject('Mux error')
                return
              }
            }
          } catch (err) {
            this.clearInterval()
            reject(err)
          }
        }, 500)
      })
    },
    clearInterval() {
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null
      }
    },
  },
}
</script>
